// Ultimate Card Game (UCG) - AI Match Telemetry Client
//
// Gathers lightweight, anonymized telemetry from completed singleplayer matches
// to inform balance updates and AI counter-matrix tuning.
//
// INVARIANT & SAFETY:
// 1. Read-only: Does not mutate game state, RNG seeds, or card definitions.
// 2. Spam filter: Matches under 30 seconds are immediately discarded before sending.
// 3. Fail-silent: Connection errors or offline status never interrupt or lag the game.

const STORAGE_KEY_SESSION = 'ucg_ai_telemetry_session';

/**
 * Returns a stable anonymous session ID for the current browser session.
 * Does not store any PII (Personally Identifiable Information).
 */
export function getTelemetrySessionId() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      let id = sessionStorage.getItem(STORAGE_KEY_SESSION);
      if (!id) {
        id = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        sessionStorage.setItem(STORAGE_KEY_SESSION, id);
      }
      return id;
    }
  } catch {
    // sessionStorage disabled or unavailable
  }
  return `anon_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Normalizes deck representations (array of card objects or strings) to a clean string array.
 * @param {Array<string|Object>} deck
 * @returns {string[]}
 */
export function normalizeDeckNames(deck) {
  if (!Array.isArray(deck)) return [];
  return deck
    .map(c => {
      if (typeof c === 'string') return c;
      if (c && typeof c.name === 'string') return c.name;
      return null;
    })
    .filter(Boolean);
}

/**
 * Sends anonymized singleplayer AI match telemetry.
 * Discards matches shorter than 30s.
 *
 * @param {Object} options
 * @param {string} options.difficulty AI difficulty ('easy', 'medium', 'hard', etc.)
 * @param {string|null} [options.aiDoctrine] AI doctrine ('rush', 'bastion', etc.)
 * @param {number} [options.optimizationFactor] 0.0 to 1.0
 * @param {'player'|'ai'} options.winner Winner of the match
 * @param {number} options.durationSeconds Match duration in seconds
 * @param {string} [options.playerRace] 'Human' or 'Sul'
 * @param {string} [options.aiRace] 'Human' or 'Sul'
 * @param {Array} options.playerDeck Cards in player's deck
 * @param {Array} options.aiDeck Cards in AI's deck
 * @param {string|null} [options.aiThreatDetected] Threat classification ('combined', 'air', etc.)
 * @param {Object} [options.statsSummary] Optional combat/damage metrics
 * @param {string} [options.endpointUrl] Target endpoint (defaults to 'api/telemetry.php')
 * @returns {boolean} True if sent or queued, false if rejected by filter or failed
 */
export function sendAiMatchTelemetry({
  difficulty = 'hard',
  aiMetadata = null,
  aiDoctrine = null,
  optimizationFactor = 0.0,
  winner = 'player',
  durationSeconds = 0,
  playerRace = 'Human',
  aiRace = 'Sul',
  playerDeck = [],
  aiDeck = [],
  aiThreatDetected = null,
  statsSummary = {},
  endpointUrl = 'api/telemetry.php'
} = {}) {
  const duration = Math.floor(Number(durationSeconds) || 0);

  // Spam Protection: Discard matches under 30 seconds immediately
  if (duration < 30) {
    return false;
  }

  const effectiveDoctrine = aiMetadata?.doctrineId || aiDoctrine || null;
  const effectiveOptimization = typeof aiMetadata?.optimizationFactor === 'number'
    ? aiMetadata.optimizationFactor
    : (Number(optimizationFactor) || 0.0);
  const effectiveThreat = aiMetadata?.threatDetected || aiThreatDetected || null;

  const normPlayerDeck = normalizeDeckNames(playerDeck);
  const normAiDeck = normalizeDeckNames(aiDeck);
  const sortedPlayerDeck = normPlayerDeck.slice().sort();
  const playerDeckFingerprint = sortedPlayerDeck.join('|');

  const payload = {
    client_session_id: getTelemetrySessionId(),
    match_id: `m_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    difficulty: String(difficulty || 'hard'),
    ai_doctrine: effectiveDoctrine ? String(effectiveDoctrine) : null,
    optimization_factor: effectiveOptimization,
    winner: winner === 'ai' ? 'ai' : 'player',
    duration_seconds: duration,
    player_race: String(playerRace || 'Human'),
    ai_race: String(aiRace || 'Sul'),
    player_deck: normPlayerDeck,
    ai_deck: normAiDeck,
    player_deck_fingerprint: playerDeckFingerprint,
    ai_threat_detected: effectiveThreat ? String(effectiveThreat) : null,
    telemetry_extra: {
      ...(statsSummary && typeof statsSummary === 'object' ? statsSummary : {}),
      ...(aiMetadata?.hasWinStreakRelief ? { hasWinStreakRelief: true, flexSlotsCount: aiMetadata.flexSlotsCount || 0 } : {}),
      ...(Array.isArray(aiMetadata?.gambits) ? { gambits: aiMetadata.gambits } : {}),
      ...(Array.isArray(aiMetadata?.observedPlayerCombos) ? { observedPlayerCombos: aiMetadata.observedPlayerCombos } : {})
    }
  };

  const jsonString = JSON.stringify(payload);

  try {
    // 1. Prefer navigator.sendBeacon (standard for non-blocking telemetry on match end/unload)
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([jsonString], { type: 'application/json' });
      const queued = navigator.sendBeacon(endpointUrl, blob);
      if (queued) return true;
    }

    // 2. Fallback to fetch with keepalive: true
    if (typeof fetch === 'function') {
      fetch(endpointUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: jsonString,
        keepalive: true
      }).catch(() => {
        // Fail-silent
      });
      return true;
    }
  } catch {
    // Fail-silent: Never throw or disrupt user gameplay
  }

  return false;
}
